import React, { useCallback, useMemo } from 'react';
import { useTimeZoneContext } from '@ais-ui/utils';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { DatetimePickerWithInput } from '@ais-ui/ui-core';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

interface DateTimeValue {
  date: Date;
  dateString: string;
  timeString: string;
}

interface DateTimePickerFieldProps {
  value: Date | undefined;
  onChange: (value: Date | undefined) => void;
  dateLimit?: 'past' | 'future' | 'all';
  startDatetimeThreshold?: Date;
  endDatetimeThreshold?: Date;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const DateTimePickerField: React.FC<DateTimePickerFieldProps> = ({
  value,
  onChange,
  dateLimit = 'all',
  startDatetimeThreshold,
  endDatetimeThreshold,
  placeholder,
  className,
  disabled = false,
}) => {
  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();

  const localValue = useMemo(() => {
    if (!value) return undefined;

    const zonedDate = toZonedTime(value, timeZone);
    const hours = zonedDate.getHours().toString().padStart(2, '0');
    const minutes = zonedDate.getMinutes().toString().padStart(2, '0');
    const seconds = zonedDate.getSeconds().toString().padStart(2, '0');

    return {
      date: zonedDate,
      dateString: '', // This will be set by the DatetimePickerWithInput component
      timeString: `${hours}:${minutes}:${seconds}`,
    };
  }, [value, timeZone]);

  const handleChange = useCallback(
    (value: DateTimeValue | undefined) => {
      if (!value || !value.date) {
        onChange(undefined);
        return;
      }

      // Convert the local timezone date back to UTC for storage
      const utcDate = fromZonedTime(value.date, timeZone);

      // ALWAYS update the form, even with invalid values
      // Let Zod validation handle showing error messages
      onChange(utcDate);
    },
    [timeZone, onChange]
  );

  return (
    <DatetimePickerWithInput
      value={localValue}
      onChange={handleChange}
      dateTimeFormat={userDateFormat}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      startDatetimeThreshold={startDatetimeThreshold}
      endDatetimeThreshold={endDatetimeThreshold}
      timeZone={timeZone}
      dateLimit={dateLimit}
    />
  );
};
